import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import type { CustomNode, Team } from '../types';
import '../styles/custom-nodes.css';

interface CustomNodeFormPageProps {
  mode: 'create' | 'edit';
}

interface FormState {
  name: string;
  bundleName: string;
  bundleVersion: string;
  nodeName: string;
  category: string;
  description: string;
  configExample: string;
  usageInstructions: string;
  metadata: string;
  visibility: 'public' | 'team_restricted';
  visibleTeamIds: string[];
}

const EMPTY_FORM: FormState = {
  name: '',
  bundleName: '',
  bundleVersion: '',
  nodeName: '',
  category: '',
  description: '',
  configExample: '',
  usageInstructions: '',
  metadata: '',
  visibility: 'public',
  visibleTeamIds: []
};

/** Accepts either `{ success, node: {...} }` or a bare node object. */
function unwrapNode(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const obj = raw as Record<string, unknown>;
  if (obj.node && typeof obj.node === 'object') {
    return obj.node as Record<string, unknown>;
  }
  return obj;
}

export function CustomNodeFormPage({ mode }: CustomNodeFormPageProps) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [file, setFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(mode === 'edit');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [existingFileName, setExistingFileName] = useState<string | null>(null);

  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  useEffect(() => {
    void api
      .myTeams()
      .then((response) => setTeams(response.teams || []))
      .catch(() => setTeams([]));
  }, []);

  useEffect(() => {
    if (mode !== 'edit' || !id) {
      return;
    }
    let active = true;
    setLoading(true);
    api
      .customNode(id)
      .then((node: CustomNode) => {
        if (!active) {
          return;
        }
        setForm({
          name: node.name || '',
          bundleName: node.bundleName || '',
          bundleVersion: node.bundleVersion || '',
          nodeName: node.nodeName || '',
          category: node.category || '',
          description: node.description || '',
          configExample: node.configExample || '',
          usageInstructions: node.usageInstructions || '',
          metadata: node.metadata ? JSON.stringify(node.metadata, null, 2) : '',
          visibility: node.visibility || 'public',
          visibleTeamIds: node.visibleTeamIds || []
        });
        setIconPreview(node.iconDataUrl);
        setExistingFileName(node.file ? node.file.fileName : null);
      })
      .catch((err) => {
        if (active) {
          setError((err as Error).message);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [mode, id]);

  const handleExtractMetadata = () => {
    setError(null);
    setNotice(null);
    if (!form.metadata.trim()) {
      setError('Paste the NodeMetadata JSON first.');
      return;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(form.metadata);
    } catch {
      setError('Metadata is not valid JSON.');
      return;
    }
    const node = unwrapNode(parsed);
    if (!node) {
      setError('Could not read a node object from that metadata.');
      return;
    }
    setForm((current) => ({
      ...current,
      name: typeof node.name === 'string' && node.name ? node.name : current.name,
      bundleName:
        typeof node.bundleName === 'string' && node.bundleName ? node.bundleName : current.bundleName,
      bundleVersion:
        node.bundleVersion != null && node.bundleVersion !== ''
          ? String(node.bundleVersion)
          : current.bundleVersion,
      nodeName: typeof node.nodeName === 'string' && node.nodeName ? node.nodeName : current.nodeName,
      category: typeof node.category === 'string' && node.category ? node.category : current.category
    }));
    if (typeof node.icon === 'string' && node.icon.startsWith('data:')) {
      setIconPreview(node.icon);
    }
    setNotice('Filled name, bundle, node class, category, and icon from the metadata.');
  };

  const handleFileChange = (selected: File | null) => {
    setFile(selected);
    if (selected) {
      const lower = selected.name.toLowerCase();
      // Best-effort: infer bundle name + version from a "<name>-<version>.tgz" filename.
      const base = lower.replace(/\.tar\.gz$|\.tgz$|\.gz$/, '');
      const match = /^(.*)-(\d[\w.]*)$/.exec(base);
      if (match && !form.bundleName) {
        updateField('bundleName', match[1]);
      }
      if (match && !form.bundleVersion) {
        updateField('bundleVersion', match[2]);
      }
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!form.bundleName.trim() || !form.bundleVersion.trim()) {
      setError('Bundle name and bundle version are required.');
      return;
    }
    if (mode === 'create' && !file) {
      setError('A .tgz bundle is required.');
      return;
    }

    const data = new FormData();
    if (file) {
      data.append('file', file);
    }
    data.append('name', form.name);
    data.append('bundleName', form.bundleName.trim());
    data.append('bundleVersion', form.bundleVersion.trim());
    data.append('nodeName', form.nodeName);
    data.append('category', form.category);
    data.append('description', form.description);
    data.append('configExample', form.configExample);
    data.append('usageInstructions', form.usageInstructions);
    data.append('metadata', form.metadata);
    data.append('visibility', form.visibility);
    data.append(
      'visibleTeamIds',
      JSON.stringify(form.visibility === 'team_restricted' ? form.visibleTeamIds : [])
    );

    setSubmitting(true);
    try {
      const saved =
        mode === 'create'
          ? await api.createCustomNode(data)
          : await api.updateCustomNode(id as string, data);
      navigate(`/custom-nodes/${saved.id}`);
    } catch (err) {
      setError((err as Error).message);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="custom-node-form">
        <div className="loading">Loading…</div>
      </div>
    );
  }

  return (
    <div className="custom-node-form">
      <div className="custom-node-detail__breadcrumb">
        <Link to={mode === 'edit' && id ? `/custom-nodes/${id}` : '/custom-nodes'}>
          <span className="material-icons">arrow_back</span>
          {mode === 'edit' ? 'Back to node' : 'Custom Nodes'}
        </Link>
      </div>

      <h1>{mode === 'create' ? 'Share a custom node' : 'Edit custom node'}</h1>

      {error ? <div className="alert alert-error">{error}</div> : null}
      {notice ? <div className="alert alert-success">{notice}</div> : null}

      <form className="custom-node-form__form" onSubmit={handleSubmit}>
        <section className="card">
          <h2>Bundle file</h2>
          <p className="hint">
            The <code>.tgz</code> produced by <code>yarn build</code> — deployed to the flow executor.
            {mode === 'edit' ? ' Leave empty to keep the current file.' : ''}
          </p>
          <label className="button button-secondary custom-node-form__file">
            <span className="material-icons">upload_file</span>
            {file ? file.name : 'Choose .tgz file'}
            <input
              type="file"
              accept=".tgz,.tar.gz,.gz,application/gzip"
              onChange={(event) => handleFileChange(event.target.files?.[0] ?? null)}
              hidden
            />
          </label>
          {!file && existingFileName ? (
            <p className="hint">Current file: <strong>{existingFileName}</strong></p>
          ) : null}
        </section>

        <section className="card">
          <div className="cn-section-header">
            <h2>NodeMetadata</h2>
            <button type="button" className="button button-tertiary button-small" onClick={handleExtractMetadata}>
              <span className="material-icons">auto_fix_high</span>
              Extract fields
            </button>
          </div>
          <p className="hint">
            Paste the IO NodeMetadata JSON (the <code>{'{ success, node }'}</code> response or a bare node
            object). Colleagues download this to register the node in their domain; “Extract fields” fills the
            details below.
          </p>
          <textarea
            className="cn-textarea cn-textarea--mono"
            rows={8}
            placeholder='{ "success": true, "node": { "bundleName": "js-runner-node", ... } }'
            value={form.metadata}
            onChange={(event) => updateField('metadata', event.target.value)}
          />
          {iconPreview ? (
            <div className="cn-icon-preview">
              <img src={iconPreview} alt="node icon" />
              <span className="hint">Icon detected from metadata</span>
            </div>
          ) : null}
        </section>

        <section className="card">
          <h2>Details</h2>
          <div className="form-row">
            <label htmlFor="cn-name">Display name</label>
            <input
              id="cn-name"
              type="text"
              value={form.name}
              onChange={(event) => updateField('name', event.target.value)}
              placeholder="JS Executer"
            />
          </div>
          <div className="cn-form-grid">
            <div className="form-row">
              <label htmlFor="cn-bundle">Bundle name *</label>
              <input
                id="cn-bundle"
                type="text"
                value={form.bundleName}
                onChange={(event) => updateField('bundleName', event.target.value)}
                placeholder="js-runner-node"
                required
              />
            </div>
            <div className="form-row">
              <label htmlFor="cn-version">Bundle version *</label>
              <input
                id="cn-version"
                type="text"
                value={form.bundleVersion}
                onChange={(event) => updateField('bundleVersion', event.target.value)}
                placeholder="1.9"
                required
              />
            </div>
            <div className="form-row">
              <label htmlFor="cn-node">Node class</label>
              <input
                id="cn-node"
                type="text"
                value={form.nodeName}
                onChange={(event) => updateField('nodeName', event.target.value)}
                placeholder="JsRunner"
              />
            </div>
            <div className="form-row">
              <label htmlFor="cn-category">Category</label>
              <input
                id="cn-category"
                type="text"
                value={form.category}
                onChange={(event) => updateField('category', event.target.value)}
                placeholder="utility_components"
              />
            </div>
          </div>
          <div className="form-row">
            <label htmlFor="cn-desc">Description</label>
            <textarea
              id="cn-desc"
              className="cn-textarea"
              rows={2}
              value={form.description}
              onChange={(event) => updateField('description', event.target.value)}
              placeholder="What this node does and when to use it."
            />
          </div>
        </section>

        <section className="card">
          <h2>JSON config example</h2>
          <p className="hint">The node <code>data</code> configuration as wired in the flow editor.</p>
          <textarea
            className="cn-textarea cn-textarea--mono"
            rows={6}
            value={form.configExample}
            onChange={(event) => updateField('configExample', event.target.value)}
            placeholder={'{\n  "js": "callback({ output: \'Success\' });"\n}'}
          />
        </section>

        <section className="card">
          <h2>Usage &amp; deployment instructions</h2>
          <p className="hint">How to deploy the bundle and configure the node.</p>
          <textarea
            className="cn-textarea"
            rows={6}
            value={form.usageInstructions}
            onChange={(event) => updateField('usageInstructions', event.target.value)}
            placeholder="1. Deploy the .tgz to the executor. 2. Import the metadata in Flow Nodes → Metadata. 3. ..."
          />
        </section>

        <section className="card">
          <h2>Visibility</h2>
          <div className="radio-group">
            <label className="radio-option">
              <input
                type="radio"
                name="visibility"
                value="public"
                checked={form.visibility === 'public'}
                onChange={() => updateField('visibility', 'public')}
              />
              <span>
                Everyone <span className="label-optional">(anyone with access to this tab)</span>
              </span>
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name="visibility"
                value="team_restricted"
                checked={form.visibility === 'team_restricted'}
                onChange={() => updateField('visibility', 'team_restricted')}
              />
              <span>Limit to selected teams</span>
            </label>
          </div>
          {form.visibility === 'team_restricted' ? (
            <div className="card card-subtle">
              {teams.length === 0 ? (
                <p className="hint">You are not in any teams yet.</p>
              ) : (
                <div className="checkbox-grid">
                  {teams.map((team) => (
                    <label key={team.id} className="checkbox-option">
                      <input
                        type="checkbox"
                        checked={form.visibleTeamIds.includes(team.id)}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          setForm((current) => {
                            const next = new Set(current.visibleTeamIds);
                            if (checked) {
                              next.add(team.id);
                            } else {
                              next.delete(team.id);
                            }
                            return { ...current, visibleTeamIds: Array.from(next) };
                          });
                        }}
                      />
                      <span>{team.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </section>

        <div className="custom-node-form__footer">
          <button
            type="button"
            className="button button-tertiary"
            onClick={() => navigate(mode === 'edit' && id ? `/custom-nodes/${id}` : '/custom-nodes')}
          >
            Cancel
          </button>
          <button type="submit" className="button button-primary" disabled={submitting}>
            {submitting ? 'Saving…' : mode === 'create' ? 'Share node' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
